import * as z from "zod";

/**
 * The `?opid` URL param carries a "user operator id" — the first two uuid
 * segments (`xxxxxxxx-xxxx`), the short handle a superuser uses to operate as
 * another user via Login-As. The backend resolver
 * (`UserAdminService.get` in `replo-backend-core/src/services/userAdmin.ts`)
 * accepts any uuid prefix (up to the full id), but URLs carry the
 * two-segment form.
 *
 * Shared between the editor (which owns the Login-As UI/flow) and any other
 * app that links into `dashboard.replo.app` and wants the link to resolve to
 * the same actor-scoped session/sandbox a linked user or process holds
 * (e.g. the admin agent-status page linking into a canary's project).
 */
export const LOGIN_AS_PARAM = "opid";

/**
 * When present alongside a foreign `?opid`, signals that the superuser opened
 * this URL to *actively* impersonate (e.g. admin's "open dash signed in as X"
 * button), so the editor performs the token swap on load instead of only
 * offering a Connect click. A bare `?opid` (e.g. incidentally carried by a
 * shared support link) still just offers the manual Connect, so a superuser is
 * never silently impersonating from a link they didn't mean as a login-as.
 */
export const LOGIN_AS_AUTO_PARAM = "opidAuto";

/** User operator id (first two uuid segments, e.g. `123e4567-e89b`). */
export function getUserOperatorId(userId: string): string {
  return userId.split("-").slice(0, 2).join("-");
}

export const LOGIN_AS_STORAGE_KEY = "replo.debug.loginAs";

/** True while a superuser is signed in as another user in this browser. */
export function isLoginAsActiveInStorage(): boolean {
  try {
    return z
      .object({ status: z.literal("active") })
      .safeParse(
        JSON.parse(window.localStorage.getItem(LOGIN_AS_STORAGE_KEY) ?? "null"),
      ).success;
  } catch {
    return false;
  }
}
